import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@core-ui/Components/ui/tooltip';

import type { SessionContactItem, SessionContactStatus } from '@features/wizard/types/wizard.types';
import { PreviewContactsTableSkeleton } from './SessionsSkeletons';
import CompactPagination from './CompactPagination';

/** Renders a value with truncate + Shadcn tooltip that only opens
 *  when the text is actually clipped (no noisy tooltips for short content). */
const TruncatedCell: React.FC<{
  value: string;
  className?: string;
}> = ({ value, className = '' }) => {
  const spanRef = useRef<HTMLSpanElement | null>(null);
  const [isClipped, setIsClipped] = useState(false);

  useEffect(() => {
    const el = spanRef.current;
    if (el === null) {
      return;
    }
    setIsClipped(el.scrollWidth > el.clientWidth);
  }, [value]);

  const content = (
    <span ref={spanRef} className={`block truncate ${className}`}>
      {value}
    </span>
  );

  if (!isClipped || value === '—') {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[400px] break-words">
        {value}
      </TooltipContent>
    </Tooltip>
  );
};

const STATUS_STYLES: Record<SessionContactStatus, { dot: string; text: string }> = {
  noAnswer:      { dot: 'bg-cyan-500',   text: 'text-cyan-500 dark:text-cyan-400' },
  cutOff:        { dot: 'bg-red-500',    text: 'text-red-500' },
  transferError: { dot: 'bg-amber-500',  text: 'text-amber-600 dark:text-amber-500' },
  voicemail:     { dot: 'bg-purple-500', text: 'text-purple-600 dark:text-purple-400' },
  fuegoLead:     { dot: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400' },
};

const isKnownStatus = (value: unknown): value is SessionContactStatus =>
  value === 'noAnswer' || value === 'cutOff' || value === 'transferError' || value === 'voicemail' || value === 'fuegoLead';

interface PreviewContactsTableProps {
  contacts: SessionContactItem[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading: boolean;
}

const PreviewContactsTable: React.FC<PreviewContactsTableProps> = ({
  contacts,
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <PreviewContactsTableSkeleton rows={Math.min(pageSize, 8)} />;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="max-h-[400px] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <Table className="table-fixed w-full">
            <TableHeader className="sticky top-0 z-10 bg-muted/60">
              <TableRow className="border-b border-border/60 hover:bg-transparent">
                <TableHead className="font-semibold w-[110px]">
                  {t('wizard.contactsPreview.status', 'Status')}
                </TableHead>
                <TableHead className="font-semibold w-[22%]">
                  {t('wizard.contactsPreview.name', 'Name')}
                </TableHead>
                <TableHead className="font-semibold w-[22%]">
                  {t('wizard.contactsPreview.company', 'Company')}
                </TableHead>
                <TableHead className="font-semibold w-[28%]">
                  {t('wizard.contactsPreview.position', 'Position')}
                </TableHead>
                <TableHead className="font-semibold w-[20%]">
                  {t('wizard.contactsPreview.phone', 'Phone')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5} className="text-center py-10 text-sm text-muted-foreground">
                    {t('wizard.selectContacts.sessions.empty.noContacts', 'No contacts match the active filters.')}
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => {
                  const status = isKnownStatus(contact.disposition) ? contact.disposition : null;
                  const style = status !== null ? STATUS_STYLES[status] : null;
                  const cleanField = (value: string | undefined | null): string => {
                    const trimmed = (value ?? '').trim();
                    return trimmed === '' ? '—' : trimmed;
                  };
                  const name = cleanField(contact.name);
                  const company = cleanField(contact.accountName);
                  const position = cleanField(contact.jobTitle);
                  const phone = cleanField(contact.phone);
                  const duplicateCount = contact.duplicateCount ?? 1;
                  const isDuplicate = duplicateCount > 1;
                  return (
                    <TableRow key={contact.id} className="border-b border-border/40 hover:bg-muted/30">
                      <TableCell>
                        {status !== null && style !== null ? (
                          <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${style.text}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${style.dot} shrink-0`} />
                            <span className="truncate">
                              {t(`wizard.selectContacts.sessions.filter.${status}`, status)}
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <div className="min-w-0 flex-1">
                            <TruncatedCell value={name} className="font-medium" />
                          </div>
                          {isDuplicate && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                                  <AlertTriangle className="h-3 w-3" />
                                  {`×${duplicateCount}`}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[280px]">
                                {t('wizard.contactsPreview.duplicateWarning', {
                                  count: duplicateCount,
                                  defaultValue: 'This phone number appears {{count}} times across the selected lists. It will only be called once.',
                                })}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <TruncatedCell value={company} />
                      </TableCell>
                      <TableCell>
                        <TruncatedCell value={position} />
                      </TableCell>
                      <TableCell>
                        <TruncatedCell value={phone} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {totalItems > 0 && (
          <div className="border-t border-border bg-card px-3 py-3">
            <CompactPagination
              totalItems={totalItems}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={(p) => {
                onPageChange(Math.max(1, Math.min(p, Math.ceil(totalItems / pageSize))));
              }}
              onPageSizeChange={onPageSizeChange}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default PreviewContactsTable;
